<template>
  <app-page-wrapper class="pt-4">
    <div
      class="before:bg-primary-100 relative grid gap-12 before:absolute before:-left-12 before:-right-12 before:top-0 before:h-[calc(100vh-180px)] before:max-h-[600px] before:rounded-xl max-lg:before:hidden lg:grid-cols-[auto_320px] lg:pt-12 xl:grid-cols-[auto_360px] [&>*]:relative"
    >
      <div>
        <course-page-header
          class="text-primary-900 max-lg:bg-primary-100 rounded-xl max-lg:p-8"
          :course="course"
        />

        <course-page-cover
          v-if="course.teachers[0]"
          class="mt-12"
          :course="course"
          :teacher="course.teachers[0]"
        />

        <div
          class="before:-z-1 relative isolate mt-6 flex flex-col gap-16 rounded-lg py-8 before:absolute before:-left-6 before:-right-6 before:bottom-0 before:top-0 before:rounded-xl before:bg-white md:mt-16 md:gap-24 lg:mt-8"
        >
          <course-page-audience :course="course" />

          <course-page-outcomes :course="course" />

          <course-page-ratings
            v-if="course.ratings && ratings"
            :course="course"
            :ratings="ratings"
          />

          <course-page-program :course="course" />

          <course-page-teacher-details
            v-for="teacher in course.teachers"
            :key="teacher.id"
            :teacher="teacher"
          />
        </div>
      </div>

      <div class="max-lg:hidden">
        <div class="sticky-below-header grid gap-6">
          <course-page-registration-panel
            v-if="nextSession"
            :course="course"
            :session="nextSession"
          />

          <course-page-action-panel v-else :course="course" />

          <course-page-related-teacher-list-panel
            v-if="displayRelatedTeacherListPanel"
            :tag="course.tags[0]!"
          />
        </div>
      </div>
    </div>

    <div class="mt-16 flex flex-col gap-16 md:mt-24 md:gap-24">
      <course-page-other-courses-list :course="course" />
      <course-page-seo-content :course="course" />
    </div>

    <course-page-mobile-bottom-sheet
      class="lg:hidden"
      :course="course"
      :next-session="nextSession"
    />
  </app-page-wrapper>
</template>

<script lang="ts" setup>
import { useRegistrationLinkAutoResume } from "~/business-areas/registration-link/composables/registration-link.hook";
import { useCoursePageData } from "~/pages/courses/[slug]/_includes/composables/course-page.data";
import { useCoursePageMeta } from "~/pages/courses/[slug]/_includes/composables/course-page-meta.hook";
import type { AnalyticsCourseView } from "~~/model/analytics.model";

import CoursePageActionPanel from "./_includes/components/course-page-action-panel.vue";
import CoursePageAudience from "./_includes/components/course-page-audience.vue";
import CoursePageCover from "./_includes/components/course-page-cover.vue";
import CoursePageHeader from "./_includes/components/course-page-header.vue";
import CoursePageMobileBottomSheet from "./_includes/components/course-page-mobile-bottom-sheet.vue";
import CoursePageOtherCoursesList from "./_includes/components/course-page-other-courses-list.vue";
import CoursePageOutcomes from "./_includes/components/course-page-outcomes.vue";
import CoursePageRatings from "./_includes/components/course-page-ratings.vue";
import CoursePageRelatedTeacherListPanel from "./_includes/components/course-page-related-teacher-list-panel.vue";
import CoursePageSeoContent from "./_includes/components/course-page-seo-content.vue";
import CoursePageTeacherDetails from "./_includes/components/course-page-teacher-details.vue";
import CoursePageProgram from "./_includes/components/program/course-page-program.vue";
import CoursePageRegistrationPanel from "./_includes/components/registration/course-page-registration-panel.vue";

definePageMeta({
  hideQualiopiFooter: true,
  transparentHeader: false,
});

const route = useRoute("courses-slug");
const slug = route.params.slug as string;

const { course, nextSession } = await useCoursePageData(slug, {
  registration_token: route.query.registration_token as string,
});

useRegistrationLinkAutoResume({ course, session: nextSession });

const { data: ratings } = useLazyFetch(
  `/api/courses/${course.value!.id}/ratings`,
);

useCoursePageMeta({
  course,
  ratingsWithComment: computed(
    () => ratings.value?.list.filter((rating) => !!rating.comment) ?? [],
  ),
});

const analyticsContext: { course?: AnalyticsCourseView } = {
  course: course.value
    ? {
        id: course.value?.id,
        slug: course.value?.slug,
        tags: course.value?.tags.map((tag) => tag.slug),
        main_tag: course.value?.tags[0]?.slug ?? "",
      }
    : undefined,
};

defineAnalyticsContext(analyticsContext);

const displayRelatedTeacherListPanel = computed(() => {
  return (
    course.value.teachers.length === 0 &&
    !course.value?.from_cache &&
    course.value?.tags[0]
  );
});
</script>
